import {
  FaroIconButton,
  FaroMenu,
  grey,
  ThreeDotsIcon,
} from "@faro-lotv/flat-ui";
import { MenuProps } from "@mui/material";
import {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  type JSX,
} from "react";

interface ContextMenuBaseProps {
  /**
   * Whether the context-menu icon should be visible
   *
   * @default true
   */
  visible?: boolean;

  /**
   * Whether the context-menu icon should be disabled
   *
   * @default false
   */
  disabled?: boolean;

  /** Callback executed when the menu is opened/closed */
  onToggle?(open: boolean): void;

  /** Enable the dark version of the menu */
  dark?: boolean;

  /** Optional color for mouse hover of the ellipsis icon used to open the menu; undefined to use the color inherited from parent */
  iconHoverColor?: CSSProperties["color"];
}

export interface ContextMenuBaseHandle {
  /** Closes the context menu when called */
  closeContextMenu(): void;
}

/** Properties for the menu backdrop that prevent a click to be propagated to elements below */
const slotProps: MenuProps["slotProps"] = {
  backdrop: {
    onClick: (e) => e.stopPropagation(),
  },
};

/**
 * @returns a three-dots button that opens a context menu, where the menu items are fully customizable as children.
 */
export const ContextMenuBase = forwardRef<
  ContextMenuBaseHandle,
  PropsWithChildren<ContextMenuBaseProps>
>(function ContextMenuBase(
  { visible = true, dark, disabled, children, onToggle, iconHoverColor },
  ref,
): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = !!anchorEl;

  useEffect(() => {
    onToggle?.(open);
  }, [onToggle, open]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useImperativeHandle(ref, () => ({
    closeContextMenu: handleClose,
  }));

  const iconColor = dark ? grey[100] : "inherit";

  return (
    <>
      <FaroIconButton
        aria-label="expand sub-menu"
        onClick={handleClick}
        size="xs"
        margin={0.5}
        // Get both the image color and its hover color from the container
        color={iconColor}
        hoverColor={iconHoverColor ?? "inherit"}
        // Change the button color to forward the container's color to the image
        sx={{
          color: iconColor,
          visibility: visible ? "visible" : "hidden",
          ":focus-visible": {
            outline: "none",
            border: "none",
          },
        }}
        disabled={disabled}
      >
        <ThreeDotsIcon />
      </FaroIconButton>

      <FaroMenu
        aria-label="tree context menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        dark={dark}
        slotProps={slotProps}
        // This fixes the bug where the drag could start on the context menu and end in the project tree,
        // resulting in the selection of an element
        onClick={(e) => e.stopPropagation()}
        /**
         * By default, MUI Menu uses a FocusTrap that continuously enforces focus to remain
         * within the menu. But if the menu contains editable text inputs, it can prevent text selection.
         */
        disableEnforceFocus
      >
        {children}
      </FaroMenu>
    </>
  );
});
